import React, { useEffect, useState } from 'react'
import { InputNumber, Modal } from 'antd'
import CustomModal from 'components/global/CustomModal'
import CustomAvatar from 'components/global/CustomAvatar'
import { myStore } from 'state/store'
import {
  useUpdateLeagueFoul,
  useUpdateLeagueGoal,
  useUpdateLeagueTimeout,
  useDeleteLeagueFoul,
  useDeleteLeagueGoal,
  useDeleteLeagueTimeout,
} from 'api/league/leagueLog/leagueLog'
import { useGetTeamMembers } from 'api/Team/Team'
import { LeagueFoulResp, LeagueGoalResp, LeagueTimeoutResp, TeamKind } from 'types/league/leagueLog'
import { TeamMemberRecord } from 'types/team/team'

// ── Shared sub-components (mirrors EnterLog) ──────────────────────────────────

const abbrevName = (first = '', last = '') => {
  const f = first.trim()
  const l = last.trim()
  if (!f && !l) return '—'
  return f ? `${f[0]}. ${l}` : l
}

const TeamCard = ({
  name,
  photo,
  label,
  selected,
  disabled,
  onClick,
}: {
  name: string
  photo?: string
  label: string
  selected: boolean
  disabled?: boolean
  onClick: () => void
}) => (
  <div
    className="flex flex-column align-center radius-12"
    style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1 }}
    onClick={disabled ? undefined : onClick}
  >
    <div
      className="flex flex-column align-center justify-center px-15px py-12px radius-12 w-full"
      style={{
        border: selected ? '2px solid #E69741' : '1px solid #EBEDF1',
        background: selected ? '#FFF8F0' : '#fff',
        minHeight: 95,
        gap: 12,
      }}
    >
      <CustomAvatar size={40} src={photo} name={name} />
      <span style={{ fontSize: 12, fontWeight: 600, color: '#000229' }}>{name}</span>
    </div>
    <span style={{ fontSize: 12, fontWeight: 600, color: '#908F98', textAlign: 'center', padding: '6px 0' }}>
      {label}
    </span>
  </div>
)

const JerseyCard = ({
  member,
  jerseyNum,
  selected,
  onClick,
}: {
  member: TeamMemberRecord
  jerseyNum: number
  selected: boolean
  onClick: () => void
}) => {
  const name = abbrevName(member.member.firstName, member.member.lastName)
  return (
    <div
      className="flex flex-column align-center justify-center pointer radius-12"
      style={{
        border: selected ? '2px solid #E69741' : '1px solid #EBEDF1',
        background: selected ? '#FFF8F0' : '#fff',
        padding: '12px 8px',
        gap: 8,
        flex: '1 0 0',
        minWidth: 0,
      }}
      onClick={onClick}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 8,
          background: selected ? '#E69741' : '#E8E8EA',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          fontWeight: 700,
          color: selected ? '#fff' : '#000',
          flexShrink: 0,
        }}
      >
        {jerseyNum}
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: '#000229',
          textAlign: 'center',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </span>
    </div>
  )
}

const PlayerGrid = ({
  members,
  selected,
  multi,
  onToggle,
}: {
  members: TeamMemberRecord[]
  selected: string[]
  multi: boolean
  onToggle: (id: string) => void
}) => {
  if (!members.length)
    return <p className="text-gray text-14 mt-10px">No players in this team</p>

  const rows: TeamMemberRecord[][] = []
  for (let i = 0; i < members.length; i += 3) rows.push(members.slice(i, i + 3))

  return (
    <div className="flex flex-column gap-10px mt-10px">
      {rows.map((row, ri) => (
        <div key={ri} className="flex gap-10px">
          {row.map((m, ci) => (
            <JerseyCard
              key={m._id}
              member={m}
              jerseyNum={ri * 3 + ci + 1}
              selected={selected.includes(m.memberId)}
              onClick={() => onToggle(m.memberId)}
            />
          ))}
          {row.length < 3 &&
            Array.from({ length: 3 - row.length }).map((_, i) => (
              <div key={`pad-${i}`} style={{ flex: '1 0 0', minWidth: 0 }} />
            ))}
        </div>
      ))}
    </div>
  )
}

// ── Types ─────────────────────────────────────────────────────────────────────

type EditableFoul    = { kind: 'foul';    data: LeagueFoulResp }
type EditableGoal    = { kind: 'goal';    data: LeagueGoalResp }
type EditableTimeout = { kind: 'timeout'; data: LeagueTimeoutResp }
export type EditableItem = EditableFoul | EditableGoal | EditableTimeout

export type TeamInfo = {
  name: string
  leagueTeamId: string  // leagueTeam._id → sent as `teamId` in PATCH payload
  teamId: string        // leagueTeam.team._id → used for useGetTeamMembers
  photo?: string
}

interface EditLogModalProps {
  item: EditableItem
  homeTeam: TeamInfo
  awayTeam: TeamInfo
  onClose: () => void
}

// ── Main component ────────────────────────────────────────────────────────────

const EditLogModal: React.FC<EditLogModalProps> = ({ item, homeTeam, awayTeam, onClose }) => {
  const setModalBtnLoader = myStore(state => state.setModalBtnLoader)

  const homeName  = homeTeam.name  || 'Home'
  const awayName  = awayTeam.name  || 'Away'
  const homePhoto = homeTeam.photo
  const awayPhoto = awayTeam.photo

  const { mutate: updateFoul }    = useUpdateLeagueFoul()
  const { mutate: updateGoal }    = useUpdateLeagueGoal()
  const { mutate: updateTimeout } = useUpdateLeagueTimeout()
  const { mutate: deleteFoul }    = useDeleteLeagueFoul()
  const { mutate: deleteGoal }    = useDeleteLeagueGoal()
  const { mutate: deleteTimeout } = useDeleteLeagueTimeout()

  // ── Foul state ──────────────────────────────────────────────────────────────
  const [foulTeam,           setFoulTeam]           = useState<TeamKind>(item.kind === 'foul' ? item.data.teamKind    : 'home')
  const [foulLeagueTeamId,   setFoulLeagueTeamId]   = useState<string>(item.kind === 'foul'    ? item.data.leagueTeamId : homeTeam.leagueTeamId)
  const [foulPlayerIds,      setFoulPlayerIds]      = useState<string[]>(item.kind === 'foul'  ? item.data.foulPlayerIds    : [])
  const [againstPlayerIds,   setAgainstPlayerIds]   = useState<string[]>(item.kind === 'foul'  ? item.data.againstPlayerIds : [])

  // ── Goal state ──────────────────────────────────────────────────────────────
  const [goalTeam,         setGoalTeam]         = useState<TeamKind>(item.kind === 'goal' ? item.data.teamKind    : 'home')
  const [goalLeagueTeamId, setGoalLeagueTeamId] = useState<string>(item.kind === 'goal'   ? item.data.leagueTeamId : homeTeam.leagueTeamId)
  const [goalPlayer,       setGoalPlayer]       = useState<string | null>(
    item.kind === 'goal' ? (item.data.playerId ?? null) : null,
  )
  const [goalScore, setGoalScore] = useState<number | null>(
    item.kind === 'goal' ? item.data.score : null,
  )

  // ── Timeout state ───────────────────────────────────────────────────────────
  const [timeoutTeam,         setTimeoutTeam]         = useState<TeamKind>(item.kind === 'timeout' ? item.data.teamKind    : 'home')
  const [timeoutLeagueTeamId, setTimeoutLeagueTeamId] = useState<string>(item.kind === 'timeout'   ? item.data.leagueTeamId : homeTeam.leagueTeamId)
  const [duration,            setDuration]            = useState<number | null>(
    item.kind === 'timeout' ? (item.data.duration || null) : null,
  )

  // ── Team members ────────────────────────────────────────────────────────────
  const { data: homeMembers, refetch: refetchHome } = useGetTeamMembers(homeTeam.teamId, {})
  const { data: awayMembers, refetch: refetchAway } = useGetTeamMembers(awayTeam.teamId, {})

  useEffect(() => {
    if (homeTeam.teamId) refetchHome()
    if (awayTeam.teamId) refetchAway()
  }, [homeTeam.teamId, awayTeam.teamId])

  const homePlayers = homeMembers?.records || []
  const awayPlayers = awayMembers?.records || []

  const foulPlayers   = foulTeam === 'home' ? homePlayers : awayPlayers
  const againstPlayers = foulTeam === 'home' ? awayPlayers : homePlayers
  const goalPlayers   = goalTeam === 'home' ? homePlayers : awayPlayers

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = () => {
    Modal.confirm({
      title: `Delete this ${item.kind}?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        if (item.kind === 'foul')    deleteFoul(item.data._id,    { onSuccess: onClose })
        if (item.kind === 'goal')    deleteGoal(item.data._id,    { onSuccess: onClose })
        if (item.kind === 'timeout') deleteTimeout(item.data._id, { onSuccess: onClose })
      },
    })
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = () => {
    setModalBtnLoader(true)

    const done = () => { setModalBtnLoader(false); onClose() }
    const fail = () => setModalBtnLoader(false)

    if (item.kind === 'foul') {
      updateFoul(
        { id: item.data._id, teamId: foulLeagueTeamId, foulPlayerIds, againstPlayerIds, teamKind: foulTeam },
        { onSuccess: done, onError: fail },
      )
    } else if (item.kind === 'goal') {
      updateGoal(
        {
          id: item.data._id,
          teamId: goalLeagueTeamId,
          teamKind: goalTeam,
          ...(goalPlayer ? { playerId: goalPlayer } : {}),
          ...(goalScore  ? { score: goalScore }     : {}),
        },
        { onSuccess: done, onError: fail },
      )
    } else if (item.kind === 'timeout') {
      updateTimeout(
        { id: item.data._id, teamId: timeoutLeagueTeamId, teamKind: timeoutTeam, duration: duration ?? undefined },
        { onSuccess: done, onError: fail },
      )
    }
  }

  const titleMap = { foul: 'Edit Foul', goal: 'Edit Goal', timeout: 'Edit Timeout' }

  const footer = (
    <div className="flex justify-between align-center">
      <button
        onClick={handleDelete}
        style={{
          background: 'transparent',
          border: '1px solid #ff4d4f',
          color: '#ff4d4f',
          borderRadius: 8,
          padding: '8px 20px',
          fontWeight: 600,
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        Delete
      </button>
      <button
        onClick={handleSave}
        style={{
          background: '#2BA7CA',
          border: 'none',
          color: '#fff',
          borderRadius: 8,
          padding: '8px 28px',
          fontWeight: 600,
          fontSize: 14,
          cursor: 'pointer',
        }}
      >
        Save
      </button>
    </div>
  )

  return (
    <CustomModal
      openModal
      isCloseModalCustom
      closeModal={onClose}
      title={titleMap[item.kind]}
      footer={footer}
    >
      {/* ── FOUL ──────────────────────────────────────────────────────────── */}
      {item.kind === 'foul' && (
        <>
          <p className="text-14 w-600 mb-10px">Team</p>
          <div className="flex gap-15px mb-20px">
            <div style={{ flex: 1 }}>
              <TeamCard
                name={homeName}
                photo={homePhoto}
                label="Home Team"
                selected={foulTeam === 'home'}
                disabled={!homeTeam.leagueTeamId}
                onClick={() => { setFoulTeam('home'); setFoulLeagueTeamId(homeTeam.leagueTeamId); setFoulPlayerIds([]); setAgainstPlayerIds([]) }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TeamCard
                name={awayName}
                photo={awayPhoto}
                label="Away Team"
                selected={foulTeam === 'away'}
                disabled={!awayTeam.leagueTeamId}
                onClick={() => { setFoulTeam('away'); setFoulLeagueTeamId(awayTeam.leagueTeamId); setFoulPlayerIds([]); setAgainstPlayerIds([]) }}
              />
            </div>
          </div>

          <p className="text-14 w-600 mb-5px">Foul Player</p>
          <p className="text-12 text-gray mb-5px">Who committed the foul</p>
          <PlayerGrid
            members={foulPlayers}
            selected={foulPlayerIds}
            multi
            onToggle={id => setFoulPlayerIds(prev =>
              prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
            )}
          />

          {againstPlayers.length > 0 && (
            <div className="mt-20px">
              <p className="text-14 w-600 mb-5px">Fouled against (optional)</p>
              <p className="text-12 text-gray mb-5px">Players from the opposing team</p>
              <PlayerGrid
                members={againstPlayers}
                selected={againstPlayerIds}
                multi
                onToggle={id => setAgainstPlayerIds(prev =>
                  prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
                )}
              />
            </div>
          )}
        </>
      )}

      {/* ── GOAL ──────────────────────────────────────────────────────────── */}
      {item.kind === 'goal' && (
        <>
          <p className="text-14 w-600 mb-10px">Team</p>
          <div className="flex gap-15px mb-20px">
            <div style={{ flex: 1 }}>
              <TeamCard
                name={homeName}
                photo={homePhoto}
                label="Home Team"
                selected={goalTeam === 'home'}
                disabled={!homeTeam.leagueTeamId}
                onClick={() => { setGoalTeam('home'); setGoalLeagueTeamId(homeTeam.leagueTeamId); setGoalPlayer(null) }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TeamCard
                name={awayName}
                photo={awayPhoto}
                label="Away Team"
                selected={goalTeam === 'away'}
                disabled={!awayTeam.leagueTeamId}
                onClick={() => { setGoalTeam('away'); setGoalLeagueTeamId(awayTeam.leagueTeamId); setGoalPlayer(null) }}
              />
            </div>
          </div>

          <p className="text-14 w-600 mb-5px">Player (optional)</p>
          <PlayerGrid
            members={goalPlayers}
            selected={goalPlayer ? [goalPlayer] : []}
            multi={false}
            onToggle={id => setGoalPlayer(prev => prev === id ? null : id)}
          />

          <div className="mt-20px">
            <p className="text-14 w-600 mb-5px">Score</p>
            <InputNumber
              min={1}
              className="br-12 w-full"
              value={goalScore}
              onChange={v => setGoalScore(v)}
              onWheel={e => e.currentTarget.blur()}
            />
          </div>
        </>
      )}

      {/* ── TIMEOUT ───────────────────────────────────────────────────────── */}
      {item.kind === 'timeout' && (
        <>
          <p className="text-14 w-600 mb-10px">Team</p>
          <div className="flex gap-15px mb-20px">
            <div style={{ flex: 1 }}>
              <TeamCard
                name={homeName}
                photo={homePhoto}
                label="Home Team"
                selected={timeoutTeam === 'home'}
                disabled={!homeTeam.leagueTeamId}
                onClick={() => { setTimeoutTeam('home'); setTimeoutLeagueTeamId(homeTeam.leagueTeamId) }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TeamCard
                name={awayName}
                photo={awayPhoto}
                label="Away Team"
                selected={timeoutTeam === 'away'}
                disabled={!awayTeam.leagueTeamId}
                onClick={() => { setTimeoutTeam('away'); setTimeoutLeagueTeamId(awayTeam.leagueTeamId) }}
              />
            </div>
          </div>

          <p className="text-14 w-600 mb-5px">Duration (seconds)</p>
          <InputNumber
            min={1}
            placeholder="30"
            className="br-12 w-full"
            value={duration}
            onChange={v => setDuration(v)}
            onWheel={e => e.currentTarget.blur()}
          />
        </>
      )}
    </CustomModal>
  )
}

export default EditLogModal
